import React, { useState, useEffect, FC, ReactNode } from 'react';

import PropTypes from 'prop-types';

import {
  fetchSubscriptions,
  Subscription,
  getCurrentTokenWithoutRedirect,
} from '@adsk/acs-sc-web-platform-api';
import { useSWRAlloy, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

type SubscriptionsContextValue = {
  data: null | Subscription[];
  error: null | unknown;
  loading: boolean;
  retry(): void;
  setEnabled?: (enabled: boolean) => void;
};

type SubscriptionsProviderProps = {
  children?: ReactNode;
};

/**
 * Context for current user subscriptions.
 */
const SubscriptionsContext =
  getOrCreateGlobalContext<SubscriptionsContextValue>(
    CONTEXT_NAMES.SubscriptionsContext
  );

const useSubscriptionsContext = () =>
  useContextWithValidation(SubscriptionsContext, {
    hook: 'useSubscriptions',
    provider: 'SubscriptionsProvider',
  });

export const useSubscriptions = (enable = true) => {
  const { setEnabled, ...rest } = useSubscriptionsContext();
  useEffect(() => {
    enable && setEnabled && setEnabled(true);
  }, [setEnabled, enable]);
  return rest;
};

export const SubscriptionsProvider: FC<SubscriptionsProviderProps> = ({
  children,
}) => {
  const [enabled, setEnabled] = useState(false);
  const {
    data: fetchData,
    error,
    revalidate: retry,
  } = useSWRAlloy(enabled ? '/current_user_subscriptions' : null, () =>
    getCurrentTokenWithoutRedirect().then(() => fetchSubscriptions())
  );
  const data = error?.status === 401 ? null : fetchData;
  const loading = !data && !error;

  return (
    <SubscriptionsContext.Provider
      value={{
        error,
        loading,
        data: data?.results,
        retry,
        setEnabled: enabled ? undefined : setEnabled,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
};

SubscriptionsProvider.displayName = 'SubscriptionsProvider';

SubscriptionsProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
};
